// Micro-Actions: Context-aware tiny interventions throughout the day
// Primary path: Atlas (Groq) on the server; local rules are fallback when offline / unauthenticated.

import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from './db-storage';
import { analytics, trackMicroActionsAtlasLoaded, trackMicroActionsLocalFallback } from './analytics';
import { toast } from 'sonner';
import { safeParseJson } from './storage-utils';

export interface MicroAction {
  id: string;
  type: 'grounding' | 'restraint' | 'reconnection' | 'boundary' | 'reflection';
  title: string;
  description: string;
  duration: string; // "60 seconds", "2 hours", etc.
  icon: string;
  completed: boolean;
  timestamp: number;
  context: string; // What triggered this action
  rtiState?: 'Stable' | 'Elastic' | 'High Tension';
  dueTime?: string; // Optional time to complete by
}

export interface RTIComponents {
  initiation?: number;
  latency?: number;
  emotionalDensity?: number;
  availability?: number;
  withdrawal?: number;
}

interface UserProfile {
  attachmentStyle?: 'secure' | 'anxious' | 'avoidant' | 'fearful' | 'unsure';
  relationshipGoal?: string;
}

/**
 * Generate micro-actions based on RTI state and detected patterns
 */
export function generateMicroActions(
  rtiScore: number,
  components: RTIComponents,
  timeOfDay: 'morning' | 'midday' | 'evening' | 'night'
): MicroAction[] {
  const actions: MicroAction[] = [];
  const state = rtiScore <= 35 ? 'Stable' : rtiScore <= 60 ? 'Elastic' : 'High Tension';
  const timestamp = Date.now();

  // Get user profile for personalization
  const profile: UserProfile = safeParseJson<UserProfile>(localStorage.getItem('tension_user_profile'), {});

  // High Tension Actions
  if (state === 'High Tension') {
    // Grounding exercise for high tension
    if (timeOfDay === 'morning' || timeOfDay === 'midday') {
      actions.push({
        id: `grounding-${timestamp}`,
        type: 'grounding',
        title: 'Pause Before Texting',
        description: 'Before you reach out, try this 60-second grounding exercise to center yourself.',
        duration: '60 seconds',
        icon: 'Pause',
        completed: false,
        timestamp,
        context: 'High tension detected',
        rtiState: state,
      });
    }

    // Restraint action for over-initiation
    if (components.initiation && components.initiation > 65) {
      actions.push({
        id: `restraint-${timestamp}`,
        type: 'restraint',
        title: 'Practice 2-Hour Phone-Free Window',
        description: 'Create space for natural reconnection. Put your phone away for just 2 hours.',
        duration: '2 hours',
        icon: 'PhoneOff',
        completed: false,
        timestamp,
        context: 'Over-initiation pattern detected',
        rtiState: state,
      });
    }

    // Boundary action for over-availability
    if (components.availability && components.availability > 70) {
      actions.push({
        id: `boundary-${timestamp}`,
        type: 'boundary',
        title: 'Reclaim Your Time',
        description: 'Schedule one activity just for you today. No phone checking during this time.',
        duration: '1 hour',
        icon: 'Shield',
        completed: false,
        timestamp,
        context: 'Over-availability detected',
        rtiState: state,
      });
    }
  }

  // Elastic State Actions
  if (state === 'Elastic') {
    // Withdrawal signal response
    if (components.withdrawal && components.withdrawal > 50) {
      actions.push({
        id: `reconnection-${timestamp}`,
        type: 'reconnection',
        title: 'Send One Genuine Compliment',
        description: 'Notice something specific you appreciate and share it. Keep it simple and authentic.',
        duration: '2 minutes',
        icon: 'Heart',
        completed: false,
        timestamp,
        context: 'Withdrawal signal detected',
        rtiState: state,
      });
    }

    // Response latency intervention
    if (components.latency && components.latency > 55) {
      actions.push({
        id: `reflection-${timestamp}`,
        type: 'reflection',
        title: 'Notice Without Reacting',
        description: 'They\'re taking longer to respond. Instead of spiraling, name the feeling: "I notice I feel anxious."',
        duration: '30 seconds',
        icon: 'Eye',
        completed: false,
        timestamp,
        context: 'Response latency increased',
        rtiState: state,
      });
    }

    // Emotional density management
    if (components.emotionalDensity && components.emotionalDensity > 60) {
      actions.push({
        id: `grounding-light-${timestamp}`,
        type: 'grounding',
        title: 'Lighten the Conversation',
        description: 'Next message: share something funny or light. Balance the emotional intensity.',
        duration: '1 minute',
        icon: 'Smile',
        completed: false,
        timestamp,
        context: 'Emotional density high',
        rtiState: state,
      });
    }
  }

  // Stable State Actions (maintenance)
  if (state === 'Stable') {
    actions.push({
      id: `maintenance-${timestamp}`,
      type: 'reflection',
      title: 'Notice What\'s Working',
      description: 'Take 30 seconds to identify one thing in your communication that feels good right now.',
      duration: '30 seconds',
      icon: 'Sparkles',
      completed: false,
      timestamp,
      context: 'Maintain stable patterns',
      rtiState: state,
    });
  }

  // Attachment-style specific actions
  if (profile.attachmentStyle === 'anxious') {
    if (state !== 'Stable') {
      actions.push({
        id: `anxious-${timestamp}`,
        type: 'grounding',
        title: 'Self-Soothing Check',
        description: 'Place hand on heart. Breathe. Say: "I am safe. I can handle uncertainty."',
        duration: '90 seconds',
        icon: 'Heart',
        completed: false,
        timestamp,
        context: 'Anxious attachment support',
        rtiState: state,
      });
    }
  }

  if (profile.attachmentStyle === 'avoidant') {
    if (components.withdrawal && components.withdrawal > 45) {
      actions.push({
        id: `avoidant-${timestamp}`,
        type: 'reconnection',
        title: 'Micro-Connection',
        description: 'Send one small message. It doesn\'t have to be deep. Just stay present.',
        duration: '1 minute',
        icon: 'MessageCircle',
        completed: false,
        timestamp,
        context: 'Avoidant deactivation pattern',
        rtiState: state,
      });
    }
  }

  // Time-of-day specific actions
  if (timeOfDay === 'morning') {
    if (state === 'High Tension') {
      actions.push({
        id: `morning-${timestamp}`,
        type: 'reflection',
        title: 'Morning Intention',
        description: 'Set one intention for today: "I will not chase. I will trust."',
        duration: '15 seconds',
        icon: 'Sunrise',
        completed: false,
        timestamp,
        context: 'Morning routine',
        rtiState: state,
      });
    }
  }

  if (timeOfDay === 'evening') {
    if (rtiScore > 40) {
      actions.push({
        id: `evening-${timestamp}`,
        type: 'boundary',
        title: 'Evening Wind-Down',
        description: 'No relationship analyzing after 9 PM. Your brain needs rest.',
        duration: 'Rest of evening',
        icon: 'Moon',
        completed: false,
        timestamp,
        context: 'Evening boundary',
        rtiState: state,
      });
    }
  }

  return actions;
}

/**
 * Get the time of day category
 */
export function getTimeOfDay(): 'morning' | 'midday' | 'evening' | 'night' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'midday';
  if (hour >= 17 && hour < 22) return 'evening';
  return 'night';
}

/**
 * Schedule micro-action notifications throughout the day
 */
export function scheduleMicroActionNotifications(actions: MicroAction[]) {
  // Check if notifications are enabled
  const settingsData = localStorage.getItem('tension_notification_settings');
  const settings = settingsData ? JSON.parse(settingsData) : { microActionReminders: true };

  if (!settings.microActionReminders) {
    return;
  }

  if ('Notification' in window) {
    // DEFENSIVE: Wrap Notification API access in try-catch
    try {
      if (Notification.permission === 'granted') {
        actions.forEach((action, index) => {
          // Stagger notifications (15 minutes apart)
          const delay = index * 15 * 60 * 1000;
          
          setTimeout(() => {
            // DEFENSIVE: Wrap Notification usage in try-catch
            try {
              new Notification('TENSION: Micro-Action', {
                body: action.title,
                icon: '/icon-192.png',
                badge: '/icon-192.png',
                tag: action.id,
                requireInteraction: false,
              });
            } catch (error) {
              console.debug('Notification API error:', error);
            }
          }, delay);
        });
      }
    } catch (error) {
      console.debug('Notification API permission check error:', error);
    }
  }
}

/**
 * Mark a micro-action as completed
 */
export function completeMicroAction(actionId: string) {
  const actionsData = localStorage.getItem('tension_micro_actions');
  const actions: MicroAction[] = actionsData ? JSON.parse(actionsData) : [];

  const updated = actions.map(action =>
    action.id === actionId
      ? { ...action, completed: true }
      : action
  );

  localStorage.setItem('tension_micro_actions', JSON.stringify(updated));

  // Track completion analytics
  const analyticsData = localStorage.getItem('tension_micro_action_analytics');
  const analytics = analyticsData ? JSON.parse(analyticsData) : { 
    totalCompleted: 0,
    completionRate: 0,
    typeBreakdown: {}
  };

  analytics.totalCompleted += 1;
  
  const action = actions.find(a => a.id === actionId);
  if (action) {
    analytics.typeBreakdown[action.type] = (analytics.typeBreakdown[action.type] || 0) + 1;
  }

  localStorage.setItem('tension_micro_action_analytics', JSON.stringify(analytics));

  return updated;
}

/**
 * Get today's micro-actions
 */
export function getTodaysMicroActions(): MicroAction[] {
  const actionsData = localStorage.getItem('tension_micro_actions');
  if (!actionsData) return [];

  const parsed: any[] = JSON.parse(actionsData);
  // Backward/forward compatibility:
  // older client builds may have stored non-string values for fields like `context`.
  // React will throw (error #130) if we try to render objects directly, so coerce safely.
  const actions: MicroAction[] = (parsed || []).map((a: any) => ({
    ...a,
    title: typeof a?.title === 'string' ? a.title : '',
    description: typeof a?.description === 'string' ? a.description : '',
    duration: typeof a?.duration === 'string' ? a.duration : '',
    context:
      typeof a?.context === 'string'
        ? a.context
        : a?.context != null
          ? JSON.stringify(a.context)
          : '',
    icon: typeof a?.icon === 'string' ? a.icon : 'Heart',
    completed: typeof a?.completed === 'boolean' ? a.completed : false,
    timestamp: typeof a?.timestamp === 'number' ? a.timestamp : Date.now(),
    id: typeof a?.id === 'string' ? a.id : `micro-${Date.now()}-${Math.random()}`,
  }));
  const today = new Date().setHours(0, 0, 0, 0);

  return actions.filter(action => {
    const actionDate = new Date(action.timestamp).setHours(0, 0, 0, 0);
    return actionDate === today;
  });
}

/**
 * Clear old micro-actions (older than 7 days)
 */
export function clearOldMicroActions() {
  const actionsData = localStorage.getItem('tension_micro_actions');
  if (!actionsData) return;

  const actions: MicroAction[] = JSON.parse(actionsData);
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

  const recent = actions.filter(action => action.timestamp > sevenDaysAgo);
  localStorage.setItem('tension_micro_actions', JSON.stringify(recent));
}

function notifyMicroActionsUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('tension-micro-actions-updated'));
  }
}

/** Unique recent titles (for Atlas dedupe). */
export function collectRecentMicroActionTitles(maxUnique = 50): string[] {
  try {
    const raw = localStorage.getItem('tension_micro_actions');
    if (!raw) return [];
    const arr: MicroAction[] = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const a of arr) {
      const t = typeof a?.title === 'string' ? a.title.trim() : '';
      if (!t) continue;
      const k = t.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(t);
      if (out.length >= maxUnique) break;
    }
    return out;
  } catch {
    return [];
  }
}

function readActiveRelationshipName(): string | null {
  try {
    const id = localStorage.getItem('tension_active_relationship');
    if (!id) return null;
    const relRaw = localStorage.getItem('tension_relationships');
    const arr = relRaw ? JSON.parse(relRaw) : [];
    if (!Array.isArray(arr)) return null;
    const r = arr.find((x: { id?: string }) => x && x.id === id);
    return r && typeof r.name === 'string' ? r.name : null;
  } catch {
    return null;
  }
}

function readAttachmentStyle(): string | null {
  try {
    const raw = localStorage.getItem('tension_user_profile');
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p.attachmentStyle === 'string' ? p.attachmentStyle : null;
  } catch {
    return null;
  }
}

function readCheckInStreak(): number | null {
  try {
    const s = localStorage.getItem('tension_streak');
    if (!s) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

type AtlasMicroActionRow = {
  type: string;
  title: string;
  description: string;
  duration: string;
  icon: string;
  context: string;
};

function mapAtlasRowsToMicroActions(rows: AtlasMicroActionRow[], rtiScore: number): MicroAction[] {
  const state = rtiScore <= 35 ? 'Stable' : rtiScore <= 60 ? 'Elastic' : 'High Tension';
  const base = Date.now();
  const validTypes = new Set(['grounding', 'restraint', 'reconnection', 'boundary', 'reflection']);
  return rows.map((r, i) => {
    const t = validTypes.has(r.type) ? r.type : 'reflection';
    return {
      id: `atlas-${base}-${i}-${Math.random().toString(36).slice(2, 10)}`,
      type: t as MicroAction['type'],
      title: r.title,
      description: r.description,
      duration: r.duration,
      icon: r.icon || 'Heart',
      completed: false,
      timestamp: base + i,
      context: r.context || '',
      rtiState: state,
    };
  });
}

/**
 * Replace today's micro-actions (avoids stacking duplicates from multiple refreshes).
 */
export function replaceTodaysMicroActions(incoming: MicroAction[]): void {
  const actionsData = localStorage.getItem('tension_micro_actions');
  const existing: MicroAction[] = actionsData ? JSON.parse(actionsData) : [];
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const kept = existing.filter((a) => {
    const d = new Date(a.timestamp).setHours(0, 0, 0, 0);
    return d !== todayStart;
  });
  localStorage.setItem('tension_micro_actions', JSON.stringify([...kept, ...incoming]));
  clearOldMicroActions();
  notifyMicroActionsUpdated();
}

export interface RefreshMicroActionsOptions {
  accessToken?: string | null;
  relationshipName?: string | null;
  attachmentStyle?: string | null;
  checkInStreak?: number | null;
  source?: 'daily' | 'quick' | 'home' | 'admin' | 'debug';
  /**
   * Controls fallback warning toast visibility.
   * Defaults to false for consumer pages; enabled only in admin/debug contexts.
   */
  showFallbackToast?: boolean;
}

export type RefreshMicroActionsResult = {
  source: 'atlas' | 'local';
  reason?: 'no_token' | 'api_error' | 'empty_response' | 'network' | 'unknown';
};

const ATLAS_FALLBACK_TOAST_KEY = 'tension_ma_atlas_fallback_toast_day';
const MICRO_ACTIONS_STATUS_KEY = 'tension_micro_actions_status';

type MicroActionsStatus = {
  source: 'atlas' | 'local';
  reason?: NonNullable<RefreshMicroActionsResult['reason']>;
  updatedAt: number;
};

function writeMicroActionsStatus(status: MicroActionsStatus): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(MICRO_ACTIONS_STATUS_KEY, JSON.stringify(status));
    }
  } catch {
    /* ignore */
  }
}

export function readMicroActionsStatus(): MicroActionsStatus | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(MICRO_ACTIONS_STATUS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.source !== 'atlas' && parsed.source !== 'local')) return null;
    return {
      source: parsed.source,
      reason: parsed.reason,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function maybeShowAtlasFallbackToast(
  hadToken: boolean,
  reason: NonNullable<RefreshMicroActionsResult['reason']>,
  enabled: boolean,
) {
  if (!enabled || !hadToken || reason === 'no_token') return;
  try {
    const day = new Date().toISOString().slice(0, 10);
    if (typeof localStorage !== 'undefined' && localStorage.getItem(ATLAS_FALLBACK_TOAST_KEY) === day) {
      return;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ATLAS_FALLBACK_TOAST_KEY, day);
    }
    toast.warning('Suggestions are in offline mode', {
      description:
        'Atlas could not load personalized micro-actions. Using built-in tips — check connection or try again later.',
    });
  } catch {
    /* ignore */
  }
}

function isAdminOrDebugSurface(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location?.pathname ?? '';
  if (path.startsWith('/admin') || path.startsWith('/enterprise/admin')) {
    return true;
  }
  try {
    return localStorage.getItem('tension_debug_toasts') === '1';
  } catch {
    return false;
  }
}

/**
 * Refresh micro-actions: Atlas (Groq) when signed in; otherwise local rules.
 * Replaces today's set instead of appending (fixes duplicate compounding).
 */
export async function refreshMicroActions(
  rtiScore: number,
  components: RTIComponents,
  options?: RefreshMicroActionsOptions,
): Promise<RefreshMicroActionsResult> {
  const timeOfDay = getTimeOfDay();
  // If caller explicitly passed accessToken (even null), trust that value.
  // This avoids accidentally using stale localStorage tokens after re-auth/session changes.
  const hasExplicitToken = !!options && Object.prototype.hasOwnProperty.call(options, 'accessToken');
  const token = hasExplicitToken
    ? (options?.accessToken ?? null)
    : (typeof localStorage !== 'undefined' ? localStorage.getItem('tension_access_token') : null);

  const recentTitles = collectRecentMicroActionTitles(50);
  const relationshipName = options?.relationshipName ?? readActiveRelationshipName();
  const attachmentStyle = options?.attachmentStyle ?? readAttachmentStyle();
  const checkInStreak = options?.checkInStreak ?? readCheckInStreak();
  const trigger = options?.source ?? 'home';
  const showToast = options?.showFallbackToast ?? isAdminOrDebugSurface();

  const applyLocal = (
    reason: NonNullable<RefreshMicroActionsResult['reason']>,
  ): RefreshMicroActionsResult => {
    // Do not log "no_token" — would fire on every anonymous page load.
    if (reason !== 'no_token') {
      trackMicroActionsLocalFallback({ reason, trigger });
    }
    const newActions = generateMicroActions(rtiScore, components, timeOfDay);
    replaceTodaysMicroActions(newActions);
    scheduleMicroActionNotifications(newActions);
    writeMicroActionsStatus({ source: 'local', reason, updatedAt: Date.now() });
    maybeShowAtlasFallbackToast(!!token, reason, showToast);
    return { source: 'local', reason };
  };

  if (!token) {
    return applyLocal('no_token');
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 28_000);
    const res = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/micro-actions`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
          'Content-Type': 'application/json',
          'X-User-Token': token,
        },
        body: JSON.stringify({
          rtiScore,
          components,
          timeOfDay,
          recentTitles,
          relationshipName,
          attachmentStyle,
          checkInStreak,
          source: trigger,
        }),
        signal: controller.signal,
      },
    );
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      const rows = Array.isArray(data.actions) ? data.actions : [];
      if (rows.length > 0) {
        const mapped = mapAtlasRowsToMicroActions(rows as AtlasMicroActionRow[], rtiScore);
        replaceTodaysMicroActions(mapped);
        scheduleMicroActionNotifications(mapped);
        writeMicroActionsStatus({ source: 'atlas', updatedAt: Date.now() });
        trackMicroActionsAtlasLoaded({ trigger });
        void analytics.flush();
        return { source: 'atlas' };
      }
      return applyLocal('empty_response');
    }

    return applyLocal('api_error');
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('Atlas micro-actions unavailable, using local generator:', e);
    }
    return applyLocal('network');
  }
}

/**
 * Get completion statistics
 */
export function getMicroActionStats() {
  const actionsData = localStorage.getItem('tension_micro_actions');
  if (!actionsData) {
    return {
      total: 0,
      completed: 0,
      completionRate: 0,
      streak: 0,
    };
  }

  const actions = safeParseJson<Array<MicroAction>>(actionsData, []);
  const today = getTodaysMicroActions();
  const completed = today.filter(a => a.completed).length;

  // Calculate streak (consecutive days with at least one completed action)
  let streak = 0;
  let currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  for (let i = 0; i < 30; i++) {
    const dayTimestamp = currentDate.getTime();
    const dayActions = actions.filter(a => {
      const actionDate = new Date(a.timestamp).setHours(0, 0, 0, 0);
      return actionDate === dayTimestamp;
    });

    const dayCompleted = dayActions.some(a => a.completed);
    if (dayCompleted) {
      streak++;
    } else if (i > 0) {
      // Break streak if we miss a day (except today)
      break;
    }

    currentDate.setDate(currentDate.getDate() - 1);
  }

  return {
    total: today.length,
    completed,
    completionRate: today.length > 0 ? Math.round((completed / today.length) * 100) : 0,
    streak,
  };
}

export type RealtimeCoachResult = {
  source: 'atlas' | 'local';
  risk: 'low' | 'moderate' | 'high';
  guardrail: string;
  reframedPrompt: string;
};

function localRealtimeCoach(text: string): RealtimeCoachResult {
  const normalized = text.toLowerCase();
  const highRisk = ['always', 'never', 'you make me', 'whatever'].some((token) => normalized.includes(token));
  const moderate = !highRisk && ['frustrated', 'upset', 'annoyed'].some((token) => normalized.includes(token));
  if (highRisk) {
    return {
      source: 'local',
      risk: 'high',
      guardrail: 'Pause before sending and remove blame-focused language.',
      reframedPrompt: 'I feel overwhelmed right now. Can we pause and try again in 20 minutes?',
    };
  }
  if (moderate) {
    return {
      source: 'local',
      risk: 'moderate',
      guardrail: 'Lead with validation before making requests.',
      reframedPrompt: 'I want us to solve this together. Can I share what landed hard for me?',
    };
  }
  return {
    source: 'local',
    risk: 'low',
    guardrail: 'Tone looks stable. Keep your request short and specific.',
    reframedPrompt: 'Thanks for hearing me. I care about working through this with you.',
  };
}

export async function getRealtimeEICoachSuggestion(
  text: string,
  accessToken?: string | null,
): Promise<RealtimeCoachResult> {
  const trimmed = text.trim();
  if (!trimmed) {
    return {
      source: 'local',
      risk: 'low',
      guardrail: 'Add your draft message to start coaching.',
      reframedPrompt: 'Share one sentence and I will help you reframe it.',
    };
  }

  if (!accessToken) return localRealtimeCoach(trimmed);

  try {
    const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ei/coach`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeadersForProtectedApi({ userToken: accessToken }),
      },
      body: JSON.stringify({ text: trimmed }),
    });
    const data = await res.json();
    if (!res.ok) return localRealtimeCoach(trimmed);
    return {
      source: data?.source === 'server' ? 'atlas' : 'local',
      risk: data?.risk === 'high' || data?.risk === 'moderate' ? data.risk : 'low',
      guardrail: typeof data?.guardrail === 'string' ? data.guardrail : localRealtimeCoach(trimmed).guardrail,
      reframedPrompt: typeof data?.reframedPrompt === 'string' ? data.reframedPrompt : localRealtimeCoach(trimmed).reframedPrompt,
    };
  } catch {
    return localRealtimeCoach(trimmed);
  }
}